import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import type {
  StockRequisition,
  PaginatedStockRequisitions,
  StockRequisitionFilters,
  CreateStockRequisitionPayload,
  MarkReceivedPayload,
} from '../types/stock-requisitions.types'

const STOCK_REQ_QUERY_KEY = 'stock-requisitions'

/** Список заявок с фильтрами и пагинацией. */
export const useStockRequisitions = (filters: StockRequisitionFilters = {}) => {
  return useQuery({
    queryKey: [STOCK_REQ_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedStockRequisitions> =>
      apiClient.getStockRequisitions(filters),
    staleTime: 30 * 1000,
    retry: 1,
  })
}

/** Детали одной заявки. Запрос включается, когда задан id. */
export const useStockRequisition = (id: string | null) => {
  return useQuery({
    queryKey: [STOCK_REQ_QUERY_KEY, 'detail', id],
    queryFn: async (): Promise<StockRequisition> =>
      apiClient.getStockRequisition(id as string),
    enabled: !!id,
    staleTime: 30 * 1000,
    retry: 1,
  })
}

/** Создание заявки (отделение). Одобрение далее идёт в 1С. */
export const useCreateStockRequisition = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateStockRequisitionPayload) =>
      apiClient.createStockRequisition(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [STOCK_REQ_QUERY_KEY] })
    },
  })
}

/** Подтверждение получения товара (отделение), с возможными расхождениями. */
export const useMarkStockRequisitionReceived = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: MarkReceivedPayload }) =>
      apiClient.markStockRequisitionReceived(vars.id, vars.data),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: [STOCK_REQ_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [STOCK_REQ_QUERY_KEY, 'detail', vars.id],
      })
    },
  })
}
